// La Campelière — cart drawer controller.
// AJAX add/change against Shopify's cart API + Section Rendering API refresh.
// Degrades to the standard /cart page when JS is unavailable.

(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';
  var ROOT_SELECTOR = '.cart-drawer-root';
  var DRAWER_SELECTOR = '[data-cart-drawer]';
  var SCRIM_SELECTOR = '[data-cart-drawer-scrim]';
  var CLOSE_SELECTOR = '[data-cart-drawer-close]';
  var ITEMS_SELECTOR = '[data-cart-drawer-items]';
  var TRIGGER_SELECTOR = '[data-cart-drawer-trigger]';
  var PRODUCT_FORM_SELECTOR = 'form[data-product-form]';
  var CARD_FORM_SELECTOR = 'form[data-cart-drawer-form]';
  var CART_PAGE_CHECKOUT_SELECTOR = '[data-cart-page-checkout]';
  var LINE_SELECTOR = '[data-cart-line-key]';
  var LINE_CHANGE_SELECTOR = '[data-cart-line-change]';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var ERROR_SELECTOR = '[data-cart-drawer-error]';
  var MAIN_CONTENT_ID = 'MainContent';
  var CHANGE_DEBOUNCE_MS = 300;

  var lastTrigger = null;
  var keydownHandler = null;
  var pendingChanges = {};

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function getRoot() { return document.querySelector(ROOT_SELECTOR); }
  function getDrawer() { return document.querySelector(DRAWER_SELECTOR); }

  function setBackgroundInert(on) {
    var main = document.getElementById(MAIN_CONTENT_ID);
    if (!main) return;
    if (on) main.setAttribute('inert', '');
    else main.removeAttribute('inert');
  }

  function showError() {
    var drawer = getDrawer();
    var el = drawer && drawer.querySelector(ERROR_SELECTOR);
    if (el) el.hidden = false;
  }

  function hideError() {
    var drawer = getDrawer();
    var el = drawer && drawer.querySelector(ERROR_SELECTOR);
    if (el) el.hidden = true;
  }

  function isOpen() {
    var drawer = getDrawer();
    return !!(drawer && drawer.getAttribute('aria-hidden') === 'false');
  }

  function open(trigger) {
    var drawer = getDrawer();
    var scrim = document.querySelector(SCRIM_SELECTOR);
    if (!drawer) return;
    lastTrigger = trigger || document.activeElement;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    if (scrim) scrim.classList.add('cart-drawer__scrim--open');
    drawer.classList.add('cart-drawer--open');
    document.body.classList.add('cart-drawer-is-open');
    document.body.style.overflow = 'hidden';
    setBackgroundInert(true);
    setTimeout(function () {
      var first = drawer.querySelector(CLOSE_SELECTOR);
      if (first) first.focus();
    }, 60);
    bindKeydown();
  }

  function close() {
    var drawer = getDrawer();
    var scrim = document.querySelector(SCRIM_SELECTOR);
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    drawer.classList.remove('cart-drawer--open');
    if (scrim) scrim.classList.remove('cart-drawer__scrim--open');
    document.body.classList.remove('cart-drawer-is-open');
    document.body.style.overflow = '';
    setBackgroundInert(false);
    setTimeout(function () { drawer.hidden = true; }, 320);
    unbindKeydown();
    if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
    lastTrigger = null;
  }

  function bindKeydown() {
    if (keydownHandler) return;
    keydownHandler = function (e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'Tab') trapFocus(e);
    };
    document.addEventListener('keydown', keydownHandler);
  }

  function unbindKeydown() {
    if (!keydownHandler) return;
    document.removeEventListener('keydown', keydownHandler);
    keydownHandler = null;
  }

  function trapFocus(e) {
    var drawer = getDrawer();
    if (!drawer) return;
    var nodes = drawer.querySelectorAll(FOCUSABLE);
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function refresh() {
    return fetch('/?sections=' + SECTION_ID, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data[SECTION_ID];
        if (!html) return;
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var fresh = doc.querySelector(ROOT_SELECTOR);
        var current = getRoot();
        if (!fresh || !current) return;
        var wasOpen = isOpen();
        // Preserve drawer open-state across refresh by re-applying classes after swap.
        current.innerHTML = fresh.innerHTML;
        if (wasOpen) {
          var d = getDrawer();
          var s = document.querySelector(SCRIM_SELECTOR);
          if (d) { d.hidden = false; d.setAttribute('aria-hidden', 'false'); d.classList.add('cart-drawer--open'); }
          if (s) s.classList.add('cart-drawer__scrim--open');
        }
        updateCartCount();
      })
      .catch(function () { /* swallow — drawer stays in last good state */ });
  }

  function updateCartCount() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = cart.item_count;
        var els = document.querySelectorAll('[data-cart-count], .header__cart-count');
        els.forEach(function (el) { el.textContent = '(' + count + ')'; });
        var labels = document.querySelectorAll('[data-cart-count-label]');
        labels.forEach(function (el) {
          var one = el.getAttribute('data-cart-count-label-one');
          var other = el.getAttribute('data-cart-count-label-other');
          if (count === 1 && one) el.textContent = one;
          else if (other) el.textContent = other.replace('%count%', count);
        });
      })
      .catch(function () {});
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (j) { throw j; });
      return r.json();
    });
  }

  function addItem(form) {
    var fd = new FormData(form);
    // FormData → plain object for /cart/add.js
    var payload = { items: [] };
    var entry = {};
    fd.forEach(function (value, key) { entry[key] = value; });
    if (!entry.id) return Promise.reject(new Error('missing variant id'));
    entry.quantity = parseInt(entry.quantity, 10) || 1;
    payload.items.push(entry);
    return postJSON('/cart/add.js', payload);
  }

  function changeLine(lineKey, quantity) {
    return postJSON('/cart/change.js', { id: lineKey, quantity: quantity });
  }

  function updateCartFromForm(form) {
    var updates = {};
    var inputs = form ? form.querySelectorAll('input[name^="updates["]') : [];
    inputs.forEach(function (input) {
      var match = input.name.match(/^updates\[(.+)\]$/);
      if (!match) return;
      updates[match[1]] = parseInt(input.value, 10) || 0;
    });
    return postJSON('/cart/update.js', { updates: updates });
  }

  function navigateTop(url) {
    try {
      if (window.top && window.top !== window) window.top.location.href = url;
      else window.location.href = url;
    } catch (e) {
      window.location.href = url;
    }
  }

  function flushLineChange(lineKey) {
    var pending = pendingChanges[lineKey];
    if (!pending) return;
    delete pendingChanges[lineKey];
    changeLine(lineKey, pending.quantity)
      .then(function () { return refresh(); })
      .catch(function (err) {
        console.error('cart change failed', err);
        // Resync the drawer with the real cart state, then surface the error.
        refresh().then(showError);
      });
  }

  function queueLineChange(lineKey, quantity) {
    var pending = pendingChanges[lineKey];
    if (pending) clearTimeout(pending.timer);
    pendingChanges[lineKey] = {
      quantity: quantity,
      timer: setTimeout(function () { flushLineChange(lineKey); }, CHANGE_DEBOUNCE_MS)
    };
  }

  function handleProductFormSubmit(e) {
    var form = e.target.closest(PRODUCT_FORM_SELECTOR + ', ' + CARD_FORM_SELECTOR);
    if (!form) return;
    e.preventDefault();
    var submit = form.querySelector('button[type="submit"], button[name="add"]');
    if (submit) { submit.disabled = true; submit.setAttribute('aria-busy', 'true'); }
    addItem(form)
      .then(function () { return refresh(); })
      .then(function () { open(submit || form); })
      .catch(function (err) {
        console.error('cart add failed', err);
        form.submit(); // fallback to standard form post
      })
      .finally(function () {
        if (submit) { submit.disabled = false; submit.removeAttribute('aria-busy'); }
      });
  }

  function handleLineChange(e) {
    var btn = e.target.closest(LINE_CHANGE_SELECTOR);
    if (!btn) return;
    var line = btn.closest(LINE_SELECTOR);
    if (!line) return;
    e.preventDefault();
    var key = line.getAttribute('data-cart-line-key');
    var action = btn.getAttribute('data-cart-line-change');
    var valueEl = line.querySelector('[data-cart-qty-value]');
    var current = parseInt(valueEl ? valueEl.textContent : '0', 10) || 0;
    var next = current;
    if (action === 'inc') next = current + 1;
    else if (action === 'dec') next = current - 1;
    else if (action === 'remove') next = 0;
    if (next < 0) next = 0;
    hideError();
    if (valueEl) valueEl.textContent = String(next);
    queueLineChange(key, next);
    if (action === 'remove') flushLineChange(key);
  }

  function handleTriggerClick(e) {
    var trigger = e.target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;
    e.preventDefault();
    refresh().then(function () { open(trigger); });
  }

  function handleCartPageCheckoutClick(e) {
    var link = e.target.closest(CART_PAGE_CHECKOUT_SELECTOR);
    if (!link) return;
    var form = document.getElementById('CartForm');
    if (!form) return;
    e.preventDefault();
    var url = link.href;
    link.setAttribute('aria-busy', 'true');
    updateCartFromForm(form)
      .catch(function (err) { console.error('cart update before checkout failed', err); })
      .then(function () { navigateTop(url); });
  }

  function handleScrimClick(e) {
    if (e.target.closest(SCRIM_SELECTOR)) close();
  }

  function handleCloseClick(e) {
    if (e.target.closest(CLOSE_SELECTOR)) close();
  }

  function bindEvents() {
    document.addEventListener('submit', handleProductFormSubmit);
    document.addEventListener('click', handleTriggerClick);
    document.addEventListener('click', handleScrimClick);
    document.addEventListener('click', handleCloseClick);
    document.addEventListener('click', handleLineChange);
    document.addEventListener('click', handleCartPageCheckoutClick);
  }

  ready(function () {
    if (!getRoot()) return;
    bindEvents();
  });

  window.CartDrawer = { open: open, close: close, refresh: refresh };
})();
